import React, { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";

/**
 * ScrollToTopButton — small floating action mounted on long-scroll list
 * pages (sets list, card grid).
 *
 * Visibility rule: hidden while at the top; appears once the user has
 * scrolled past ~1 viewport height. Deliberately quiet visually — the
 * page is the star; this is an escape hatch, not a CTA.
 *
 * Interaction: smooth-scrolls to the top on click. Uses `behavior:
 * "smooth"` because this button is a deliberate user request (unlike
 * the routing-driven ScrollToTop which uses `instant` to avoid mid-
 * animation surprises after taps).
 *
 * Accessibility: 44×44px hit target (Apple / WCAG mobile minimum),
 * labelled for screen readers, focus-visible ring via existing token.
 * Rendered as a plain <button> so it participates in keyboard order.
 */
export default function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => setVisible(window.scrollY > window.innerHeight);
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      data-testid="scroll-to-top-button"
      onClick={() =>
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
      }
      aria-label="Scroll to top"
      className={
        "fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center " +
        "justify-center rounded-full border border-ink-chip " +
        "bg-ink-surface/90 text-ink-text shadow-lg backdrop-blur " +
        "transition-colors duration-cc ease-cc-ease " +
        "hover:border-ink-muted hover:bg-ink-raised " +
        "focus-visible:outline-none focus-visible:ring-2 " +
        "focus-visible:ring-indigo-text"
      }
    >
      <ChevronUp size={20} aria-hidden />
    </button>
  );
}
